import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Download, Upload, Camera, MapPin, X, ListPlus, CheckCircle2, AlertTriangle, Building2, GripVertical, OctagonX, RotateCcw, ArchiveRestore, FileSpreadsheet, XCircle, Search } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "../../components/common";
import { UndoImportDialog } from "../../components/UndoImportDialog";
import { UploadPreviewTable } from "../../components/UploadPreviewTable";
import { AreaFlagsBanner } from "../../components/AreaFlagsBanner";
import { PaymentImportSection } from "../../components/PaymentImportSection";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Progress } from "../../components/ui/progress";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { Skeleton } from "../../components/ui/skeleton";
import { api, errMsg, downloadBlob, getAuth, fmtINR } from "../../lib/api";
import { compressImage } from "../../lib/imageCompress";
import { ReadOnlyNotice } from "../../components/FeatureGate";
import { Textarea } from "../../components/ui/textarea";
import { TPDemandLinesEditor, newLine, fromLookupDemand, lineChanged, linesPayload, validateLines } from "../../components/third_party/TPDemandLinesEditor";   // Phase 211
import { ImportReconciliationDialog, ReconcileButton } from "../../components/upload/ImportReconciliationDialog";   // Phase 212

const FIELD_TYPE_LABELS = { text: "Text", alphanumeric: "Alphanumeric", number: "Number", date: "Date", select: "Dropdown" };

// ---- Amount auto-calculation (Phase 66) ----
// Custom fields whose labels mention "arrear" or "dues" (case-insensitive) are treated as
// amount components, e.g. "Previous Year arrear" + "current year dues" → Amount.
const isAmountComponent = (label) => /arrear|dues/i.test(label || "");
const computeAutoAmount = (fields, customData) => {
  let total = 0;
  let found = false;
  for (const f of fields || []) {
    if (!isAmountComponent(f.label)) continue;
    const v = customData?.[f.key];
    if (v === "" || v == null) continue;
    const n = parseFloat(v);
    if (!Number.isNaN(n)) { total += n; found = true; }
  }
  return found ? Math.round(total * 100) / 100 : null;
};

// ---------------- dynamic custom-field inputs (shared by manual form) ----------------
// When `onDelete` is provided, each field shows an inline remove button (admin form builder
// shortcut) — deletion syncs the bulk template columns on the fly.
function TPFieldInputs({ fields, values, onChange, onDelete }) {
  if (!fields || fields.length === 0) return null;
  const set = (key, v) => onChange({ ...(values || {}), [key]: v });
  return (
    <div className="space-y-3 border rounded-lg p-3 bg-muted/30" data-testid="tp-custom-fields-section">
      <p className="text-xs font-medium text-muted-foreground">Third-party fields (configured for your municipality)</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {fields.map((f) => (
          <div key={f.key} className="space-y-1">
            <div className="flex items-center justify-between gap-2 min-h-5">
              <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
              {onDelete && (
                <button type="button" onClick={() => onDelete(f)}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-150 p-0.5 rounded focus-visible:ring-2 focus-visible:ring-ring"
                  title={`Delete field "${f.label}"`} data-testid={`tp-inline-delete-${f.key}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {f.field_type === "select" ? (
              <Select value={values?.[f.key] != null ? String(values[f.key]) : ""} onValueChange={(v) => set(f.key, v)}>
                <SelectTrigger data-testid={`tp-field-${f.key}`}><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                <SelectContent>
                  {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input
                type={f.field_type === "number" ? "number" : f.field_type === "date" ? "date" : "text"}
                placeholder={f.field_type === "alphanumeric" ? "e.g., ABC123" : undefined}
                value={values?.[f.key] != null ? values[f.key] : ""}
                onChange={(e) => set(f.key, e.target.value)} data-testid={`tp-field-${f.key}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Configure Fields (form builder) ----------------
function FieldDialog({ code, open, onOpenChange, field, onSaved }) {
  const isEdit = !!field;
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [optionsText, setOptionsText] = useState("");
  const [required, setRequired] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setLabel(field?.label || "");
      setFieldType(field?.field_type || "text");
      setOptionsText((field?.options || []).join(", "));
      setRequired(!!field?.required);
    }
  }, [open, field]);

  const submit = async (e) => {
    e.preventDefault();
    const options = optionsText.split(",").map((s) => s.trim()).filter(Boolean);
    if (fieldType === "select" && options.length === 0) return toast.error("Dropdown fields need at least one option");
    setBusy(true);
    try {
      const body = { label: label.trim(), field_type: fieldType, options, required };
      if (isEdit) {
        await api.put(`/t/${code}/third-party/fields/${field.id}`, body);
        toast.success(`Field "${label.trim()}" updated — form and template refreshed`);
      } else {
        await api.post(`/t/${code}/third-party/fields`, body);
        toast.success(`Field "${label.trim()}" added — it now appears in Manual Entry and the bulk template`);
      }
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="tp-field-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ListPlus className="h-4 w-4" /> {isEdit ? `Edit field — ${field?.label}` : "New third-party field"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label>Field label <span className="text-destructive">*</span></Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g., Survey Agency"
              required data-testid="tp-field-label-input" />
          </div>
          <div className="space-y-1">
            <Label>Field type</Label>
            <Select value={fieldType} onValueChange={setFieldType}>
              <SelectTrigger data-testid="tp-field-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="alphanumeric">Alphanumeric</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="select">Dropdown</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {fieldType === "alphanumeric" && (
            <p className="text-[11px] text-muted-foreground">
              Letters &amp; numbers (optionally - / _), e.g., ABC123 or GZB-W5-001.
            </p>
          )}
          {fieldType === "select" && (
            <div className="space-y-1">
              <Label>Options (comma separated)</Label>
              <Input value={optionsText} onChange={(e) => setOptionsText(e.target.value)}
                placeholder="e.g., Yes, No" data-testid="tp-field-options-input" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={required} onCheckedChange={setRequired} data-testid="tp-field-required-switch" />
            Required
          </label>
          <Button type="submit" className="w-full" disabled={busy} data-testid="tp-field-save-button">
            {busy ? "Saving..." : isEdit ? "Save field" : "Add field"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfigureFieldsSection({ code, fields, onChanged, readOnly = false }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editField, setEditField] = useState(null);
  const [deleteField, setDeleteField] = useState(null);
  // drag & drop reorder state
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const confirmDelete = async () => {
    try {
      await api.delete(`/t/${code}/third-party/fields/${deleteField.id}`);
      toast.success(`Field "${deleteField.label}" removed — form and bulk template updated`);
      setDeleteField(null);
      onChanged();
    } catch (err) { toast.error(errMsg(err)); setDeleteField(null); }
  };

  const onDrop = async (targetIndex) => {
    const from = dragIndex;
    setDragIndex(null);
    setOverIndex(null);
    if (readOnly || from == null || from === targetIndex) return;
    const next = [...fields];
    const [moved] = next.splice(from, 1);
    next.splice(targetIndex, 0, moved);
    try {
      await api.put(`/t/${code}/third-party/fields/reorder`, { order: next.map((f) => f.id) });
      toast.success("Field order updated — Manual form and bulk template columns follow the new order");
      onChanged();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-3">
      {readOnly && <ReadOnlyNotice what="the third-party field configuration" testId="tp-fields-readonly-notice" />}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Fields added here appear instantly on the Manual Entry form and as columns in the bulk template.
          {!readOnly && " Drag the handle to reorder — the form and template follow the same order."}
        </p>
        {!readOnly && <Button onClick={() => { setEditField(null); setDialogOpen(true); }} data-testid="tp-add-field-button">
          <Plus className="h-4 w-4 mr-2" /> Add Field
        </Button>}
      </div>
      {fields.length === 0 ? (
        <EmptyState title="No third-party fields yet"
          subtitle='Add fields like "Survey Agency" or "Water Connection" to capture third-party data.'
          action={readOnly ? null : <Button onClick={() => { setEditField(null); setDialogOpen(true); }} data-testid="tp-empty-add-field-button"><Plus className="h-4 w-4 mr-2" /> Add Field</Button>} />
      ) : (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <Table data-testid="tp-fields-table">
              <TableHeader>
                <TableRow>
                  {!readOnly && <TableHead className="w-8"></TableHead>}
                  <TableHead>Label</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="hidden md:table-cell">Options</TableHead>
                  <TableHead className="text-center">Required</TableHead>
                  {!readOnly && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {fields.map((f, idx) => (
                  <TableRow key={f.id} data-testid={`tp-field-row-${f.key}`}
                    draggable={!readOnly}
                    onDragStart={() => setDragIndex(idx)}
                    onDragOver={(e) => { e.preventDefault(); if (overIndex !== idx) setOverIndex(idx); }}
                    onDragLeave={() => setOverIndex((v) => (v === idx ? null : v))}
                    onDrop={() => onDrop(idx)}
                    onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                    className={`hover:bg-muted/60 ${dragIndex === idx ? "opacity-50" : ""} ${overIndex === idx && dragIndex !== null && dragIndex !== idx ? "border-t-2 border-t-primary" : ""}`}>
                    {!readOnly && <TableCell className="w-8 cursor-grab active:cursor-grabbing text-muted-foreground"
                      title="Drag to reorder" data-testid={`tp-field-drag-${f.key}`}>
                      <GripVertical className="h-4 w-4" />
                    </TableCell>}
                    <TableCell>
                      <p className="font-medium text-sm">{f.label}</p>
                      <p className="text-[11px] text-muted-foreground font-mono">{f.key}</p>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{FIELD_TYPE_LABELS[f.field_type] || f.field_type}</Badge></TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                      {f.field_type === "select" ? (f.options || []).join(", ") : "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      {f.required
                        ? <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">Required</Badge>
                        : <span className="text-xs text-muted-foreground">Optional</span>}
                    </TableCell>
                    {!readOnly && <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit field"
                        onClick={() => { setEditField(f); setDialogOpen(true); }} data-testid={`tp-field-edit-${f.key}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete field"
                        onClick={() => setDeleteField(f)} data-testid={`tp-field-delete-${f.key}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      <FieldDialog code={code} open={dialogOpen} onOpenChange={setDialogOpen} field={editField} onSaved={onChanged} />
      <AlertDialog open={!!deleteField} onOpenChange={(v) => !v && setDeleteField(null)}>
        <AlertDialogContent data-testid="tp-delete-field-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete field "{deleteField?.label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              It will disappear from the Manual Entry form and the bulk template immediately.
              Values already saved on existing properties remain stored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tp-delete-field-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="tp-delete-field-confirm-button">Delete field</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------- Manual Entry ----------------
const emptyManual = { pin: "", owner_name: "", father_spouse_name: "", mobile: "", house_no: "", address: "",
  zone_id: "", ward_id: "", mohalla_id: "", property_type_id: "", road_width_id: "", custom_data: {} };

function ManualSection({ code, fields, onChanged, readOnly = false }) {
  const [form, setForm] = useState(emptyManual);
  const [lines, setLines] = useState([]);                 // Phase 211: unified demand-lines editor
  const [reason, setReason] = useState("");               // mandatory when an existing line's amounts change
  const [zones, setZones] = useState([]);
  const [wards, setWards] = useState([]);
  const [mohallas, setMohallas] = useState([]);
  const [fys, setFys] = useState([]);
  const [taxHeads, setTaxHeads] = useState([]);
  const [ptypes, setPtypes] = useState([]);
  const [roadWidths, setRoadWidths] = useState([]);
  const [photos, setPhotos] = useState([]);       // [{...serverRef, previewUrl}]
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);   // inline field deletion
  // Phase 211: PIN search
  const [searchPin, setSearchPin] = useState("");
  const [searching, setSearching] = useState(false);
  const [lookup, setLookup] = useState(null);     // null | {found:false, pin} | {found:true, property, demands, totals}
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const existing = lookup?.found ? lookup.property : null;   // update mode when set

  const confirmDeleteField = async () => {
    const f = deleteTarget;
    try {
      await api.delete(`/t/${code}/third-party/fields/${f.id}`);
      // drop any value typed for the removed field
      setForm((prev) => {
        const cd = { ...(prev.custom_data || {}) };
        delete cd[f.key];
        return { ...prev, custom_data: cd };
      });
      toast.success(`Field "${f.label}" deleted — Manual form and bulk template columns updated`);
      setDeleteTarget(null);
      onChanged();
    } catch (err) { toast.error(errMsg(err)); setDeleteTarget(null); }
  };

  useEffect(() => {
    Promise.all([
      api.get(`/t/${code}/masters/zones`), api.get(`/t/${code}/masters/wards`),
      api.get(`/t/${code}/masters/mohallas`), api.get(`/t/${code}/masters/financial-years`),
      api.get(`/t/${code}/masters/tax-heads`),
      api.get(`/t/${code}/masters/property-types`).catch(() => ({ data: [] })),
      api.get(`/t/${code}/masters/road-widths`).catch(() => ({ data: [] })),
    ]).then(([z, w, m, f, th, pt, rw]) => {
      setZones((z.data || []).filter((x) => x.is_active !== false));
      setWards((w.data || []).filter((x) => x.is_active !== false));
      setMohallas((m.data || []).filter((x) => x.is_active !== false));
      setFys(f.data || []);
      setTaxHeads((th.data || []).filter((x) => x.is_active !== false));
      setPtypes((pt.data || []).filter((x) => x.is_active !== false));
      setRoadWidths((rw.data || []).filter((x) => x.is_active !== false));
    }).catch((e) => toast.error(errMsg(e)));
  }, [code]);

  const wardOptions = wards.filter((w) => !form.zone_id || w.zone_id === form.zone_id);
  const mohallaOptions = mohallas.filter((m) => !form.ward_id || m.ward_id === form.ward_id);
  const hasAmountComponents = (fields || []).some((f) => isAmountComponent(f.label));
  const changedLines = lines.filter(lineChanged);
  const needsReason = changedLines.length > 0;

  // Amount auto-calculation: whenever an arrear/dues component field changes, recompute the FIRST demand
  // line's Current amount = sum of components (overwrites manual edits — confirmed behavior). The input stays editable.
  const handleCustomChange = (cd) => {
    const componentChanged = (fields || []).some((f) => isAmountComponent(f.label) &&
      String(form.custom_data?.[f.key] ?? "") !== String(cd?.[f.key] ?? ""));
    setForm((prev) => ({ ...prev, custom_data: cd }));
    if (!componentChanged) return;
    const auto = computeAutoAmount(fields, cd);
    setLines((prev) => {
      const next = prev.length ? [...prev] : [newLine()];
      next[0] = { ...next[0], current_amount: auto != null ? String(auto) : "" };
      return next;
    });
  };

  const getPosition = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve(null), { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 });
  });

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only image files are allowed");
    setUploadingPhoto(true);
    try {
      const shrunk = await compressImage(file);          // Phase 130: <= 200 KB JPEG before upload
      const upload = shrunk.file || file;
      if (upload.size > 5 * 1024 * 1024) return toast.error("Photo is larger than 5 MB");
      const gps = await getPosition();
      const fd = new FormData();
      fd.append("file", upload, upload.name);
      if (gps) { fd.append("lat", String(gps.lat)); fd.append("lng", String(gps.lng)); fd.append("accuracy", String(gps.accuracy)); }
      const r = await api.post(`/t/${code}/third-party/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((prev) => [...prev, { ...r.data, previewUrl: URL.createObjectURL(upload) }]);
      toast.success(gps ? `Photo added with GPS (${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)})`
        : "Photo added — GPS unavailable (allow location access for GPS tagging)");
    } catch (err) { toast.error(errMsg(err)); } finally { setUploadingPhoto(false); }
  };

  const removePhoto = (fid) => setPhotos((prev) => prev.filter((p) => p.file_id !== fid));

  // ---- Phase 211: PIN search → auto-fill (found) or create-with-PIN (not found)
  const resetForm = (keepPin = "") => {
    setForm({ ...emptyManual, pin: keepPin });
    setLines([]);
    setReason("");
    setPhotos([]);
  };
  const clearSearch = () => { setSearchPin(""); setLookup(null); resetForm(""); };

  const doSearch = async () => {
    const pin = searchPin.trim();
    if (!pin) return toast.error("Enter a PIN to search");
    if (!/^[A-Za-z0-9][A-Za-z0-9\-/_ ]*$/.test(pin) || pin.length > 30) {
      return toast.error("PIN must be alphanumeric (letters/numbers, optionally - / _), max 30 characters");
    }
    setSearching(true);
    try {
      const r = await api.get(`/t/${code}/third-party/lookup`, { params: { pin } });
      const d = r.data;
      setLookup(d);
      if (d.found) {
        const p = d.property;
        const cd = {};
        for (const f of fields || []) cd[f.key] = p.third_party_data?.[f.key] ?? "";
        setForm({
          pin: p.pin, owner_name: p.owner_name || "", father_spouse_name: p.father_spouse_name || "", mobile: p.mobile || "",
          house_no: p.house_no || "", address: p.address || "", zone_id: p.zone_id || "", ward_id: p.ward_id || "",
          mohalla_id: p.mohalla_id || "", property_type_id: p.property_type_id || "", road_width_id: p.road_width_id || "",
          custom_data: cd,
        });
        setLines((d.demands || []).map(fromLookupDemand));
        setReason("");
        setPhotos([]);
        toast.success(`PIN ${p.pin} found — ${p.owner_name || "property"} loaded with ${d.demands?.length || 0} demand line${d.demands?.length === 1 ? "" : "s"}`, {
          description: "Edit the details or demand lines, then save. New lines create demands; changed amounts are recorded as corrections (reason required).",
        });
      } else {
        resetForm(d.pin);
        toast.info(`No property with PIN ${d.pin} — fill the form to create it with this PIN`);
      }
    } catch (err) { toast.error(errMsg(err)); } finally { setSearching(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (readOnly) return toast.error("View only — you do not have permission to create or update third-party properties");
    if (!form.owner_name.trim()) return toast.error("Owner name is required");
    if (!existing && form.pin.trim() && !/^[A-Za-z0-9][A-Za-z0-9\-/_ ]*$/.test(form.pin.trim())) {
      return toast.error("PIN must be alphanumeric (letters/numbers, optionally - / _)");
    }
    if (!form.ward_id) return toast.error("Select a Ward — it decides which Tax Collector gets this property");
    const lineErr = validateLines(lines);
    if (lineErr) return toast.error(lineErr);
    if (needsReason && reason.trim().length < 5) {
      return toast.error(`You changed ${changedLines.length} existing demand line${changedLines.length === 1 ? "" : "s"} — enter the reason for the correction (at least 5 characters)`);
    }
    // required third-party fields bind new properties and properties created through Third Party
    if (!existing || existing.source === "third_party") {
      for (const f of fields) {
        const v = form.custom_data?.[f.key];
        if (f.required && (v == null || String(v).trim() === "")) return toast.error(`'${f.label}' is required`);
      }
    }
    setBusy(true);
    try {
      const common = {
        owner_name: form.owner_name, father_spouse_name: form.father_spouse_name, mobile: form.mobile, house_no: form.house_no,
        address: form.address, zone_id: form.zone_id || null, ward_id: form.ward_id, mohalla_id: form.mohalla_id || null,
        property_type_id: form.property_type_id || null, road_width_id: form.road_width_id || null,
        custom_data: form.custom_data, photos: photos.map(({ previewUrl, ...p }) => p),
        demand_lines: linesPayload(lines),
      };
      if (existing) {
        const r = await api.put(`/t/${code}/third-party/manual/${existing.id}`, { ...common, reason: needsReason ? reason.trim() : null });
        const d = r.data;
        const bits = [];
        if (d.property_updated) bits.push("details saved");
        if (d.demands_created) bits.push(`${d.demands_created} demand line${d.demands_created === 1 ? "" : "s"} added`);
        if (d.demands_corrected) bits.push(`${d.demands_corrected} line${d.demands_corrected === 1 ? "" : "s"} corrected`);
        if (d.photos_added) bits.push(`${d.photos_added} photo${d.photos_added === 1 ? "" : "s"} added`);
        toast.success(`Property ${d.pin} updated${bits.length ? ` — ${bits.join(", ")}` : " — nothing changed"}`, { duration: 8000 });
        // reload the fresh state so the editor shows the saved lines (new ids, balances)
        setSearchPin(d.pin);
        const r2 = await api.get(`/t/${code}/third-party/lookup`, { params: { pin: d.pin } });
        setLookup(r2.data);
        setLines((r2.data.demands || []).map(fromLookupDemand));
        setReason("");
        setPhotos([]);
      } else {
        const r = await api.post(`/t/${code}/third-party/manual`, { ...common, pin: form.pin });
        const d = r.data;
        toast.success(
          `Property ${d.pin} created${d.assigned_collector_name ? ` — assigned to ${d.assigned_collector_name}` : " — no collector covers this ward yet (assign via Supervisor)"}${d.demands_created ? `. ${d.demands_created} demand line${d.demands_created === 1 ? "" : "s"} raised.` : ""}`,
          { duration: 8000 });
        clearSearch();
      }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  return (
    <Card className="shadow-sm max-w-4xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading flex items-center gap-2 flex-wrap">
          <Building2 className="h-4 w-4" /> {existing ? "Update third-party property" : "New third-party property"}
          <Badge variant="outline" className={existing ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"} data-testid="tp-mode-badge">
            {existing ? `Updating existing · ${existing.pin}` : "New property"}
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Search a PIN first: an existing property is loaded for editing (details + demand lines); an unknown PIN pre-fills the form so you can create the property with that PIN.
          New properties are created in the Register (source: Third Party) and auto-assigned to the Tax Collector covering the ward.
        </p>
      </CardHeader>
      <CardContent>
        {readOnly && <ReadOnlyNotice what="the third-party manual entry form" testId="tp-manual-readonly-notice" className="mb-4" />}

        {/* Phase 211: PIN search */}
        <div className="rounded-lg border bg-muted/30 p-3 mb-4 space-y-2" data-testid="tp-pin-search">
          <Label htmlFor="tp-pin-search-input" className="flex items-center gap-1.5"><Search className="h-4 w-4" /> Search by PIN</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input id="tp-pin-search-input" value={searchPin} onChange={(e) => setSearchPin(e.target.value)} maxLength={30} className="font-mono uppercase placeholder:normal-case"
              placeholder="e.g., BMC100001 — enter an existing PIN to load the property" disabled={searching}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doSearch(); } }} data-testid="tp-pin-search-input" />
            <div className="flex gap-2">
              <Button type="button" onClick={doSearch} disabled={searching || !searchPin.trim()} className="min-w-[110px]" data-testid="tp-pin-search-button">
                <Search className="h-4 w-4 mr-1.5" /> {searching ? "Searching…" : "Search"}
              </Button>
              {(lookup || searchPin) && (
                <Button type="button" variant="outline" onClick={clearSearch} disabled={searching} data-testid="tp-pin-search-clear">
                  <X className="h-4 w-4 mr-1.5" /> Clear
                </Button>
              )}
            </div>
          </div>
          {lookup && (lookup.found ? (
            <div className="rounded-md border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-900 space-y-1" data-testid="tp-pin-found-banner">
              <p className="font-medium flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> PIN {lookup.property.pin} found — {lookup.property.owner_name || "no owner name"}
                <span className="font-normal text-sky-800">· {[lookup.property.ward_name, lookup.property.mohalla_name].filter(Boolean).join(", ") || "ward not set"}</span>
                <Badge variant="outline" className="text-[10px] font-normal bg-white/70 capitalize">{(lookup.property.source || "").replace(/_/g, " ") || "register"}</Badge>
                {lookup.property.status && lookup.property.status !== "active" && <Badge variant="outline" className="text-[10px] font-normal bg-amber-50 text-amber-800 border-amber-200 capitalize">{lookup.property.status}</Badge>}
              </p>
              <p className="text-sky-800 tabular-nums" data-testid="tp-pin-found-summary">
                {lookup.demands.length} demand line{lookup.demands.length === 1 ? "" : "s"} · demand {fmtINR(lookup.totals?.demand || 0)} · paid {fmtINR(lookup.totals?.paid || 0)} · balance {fmtINR(lookup.totals?.balance || 0)}
                {lookup.property.assigned_collector_name ? ` · collector ${lookup.property.assigned_collector_name}` : " · no collector assigned"}
                {lookup.property.updated_at ? ` · last updated ${fmtWhen(lookup.property.updated_at)}` : ""}
              </p>
            </div>
          ) : (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-900 flex items-start gap-1.5" data-testid="tp-pin-notfound-banner">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>No property has PIN <b className="font-mono">{lookup.pin}</b>. Fill in the details below and the property will be created with this PIN.
                {lookup.pin_valid === false && <b className="block text-amber-800">This PIN has characters that are not allowed — use letters, numbers and - / _ only.</b>}</span>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <fieldset disabled={readOnly} className="space-y-4 min-w-0" data-testid="tp-manual-fieldset">
          <div className="space-y-1">
            <Label>PIN (alphanumeric, optional)</Label>
            <Input value={form.pin} onChange={set("pin")} maxLength={30} readOnly={!!existing} className={existing ? "font-mono bg-muted/50" : "font-mono"}
              placeholder="e.g., GZB-W5-00123 — leave blank to auto-generate"
              data-testid="tp-pin-input" />
            <p className="text-[11px] text-muted-foreground">
              {existing ? "The PIN of an existing property cannot be changed here." : "Letters & numbers (optionally - / _). Leave blank and the system will generate the next PIN automatically."}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Owner name <span className="text-destructive">*</span></Label>
              <Input value={form.owner_name} onChange={set("owner_name")} required data-testid="tp-owner-input" />
            </div>
            <div className="space-y-1">
              <Label>Father / Spouse name</Label>
              <Input value={form.father_spouse_name} onChange={set("father_spouse_name")} data-testid="tp-father-input" />
            </div>
            <div className="space-y-1">
              <Label>Mobile</Label>
              <Input value={form.mobile} onChange={set("mobile")} maxLength={10} data-testid="tp-mobile-input" />
            </div>
            <div className="space-y-1">
              <Label>House No</Label>
              <Input value={form.house_no} onChange={set("house_no")} data-testid="tp-houseno-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Address</Label>
            <Input value={form.address} onChange={set("address")} data-testid="tp-address-input" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Zone <span className="text-muted-foreground font-normal text-[11px]">(optional)</span></Label>
              <Select value={form.zone_id} onValueChange={(v) => setForm({ ...form, zone_id: v, ward_id: "", mohalla_id: "" })}>
                <SelectTrigger data-testid="tp-zone-select"><SelectValue placeholder="Optional — auto-set from ward" /></SelectTrigger>
                <SelectContent>
                  {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.zone_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Ward <span className="text-destructive">*</span></Label>
              <Select value={form.ward_id} onValueChange={(v) => {
                const w = wards.find((x) => x.id === v);
                setForm({ ...form, ward_id: v, mohalla_id: "", zone_id: form.zone_id || w?.zone_id || "" });
              }}>
                <SelectTrigger data-testid="tp-ward-select"><SelectValue placeholder="Select ward" /></SelectTrigger>
                <SelectContent>
                  {wardOptions.map((w) => <SelectItem key={w.id} value={w.id}>{w.ward_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Mohalla</Label>
              <Select value={form.mohalla_id} onValueChange={(v) => setForm({ ...form, mohalla_id: v })}>
                <SelectTrigger data-testid="tp-mohalla-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {mohallaOptions.map((m) => <SelectItem key={m.id} value={m.id}>{m.mohalla_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Phase 211: property type + road width (optional) */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Property type <span className="text-muted-foreground font-normal text-[11px]">(optional)</span></Label>
              <Select value={form.property_type_id} onValueChange={(v) => setForm({ ...form, property_type_id: v })}>
                <SelectTrigger data-testid="tp-ptype-select"><SelectValue placeholder="Select property type" /></SelectTrigger>
                <SelectContent>
                  {ptypes.map((p) => <SelectItem key={p.id} value={p.id}>{p.type_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Road width <span className="text-muted-foreground font-normal text-[11px]">(optional)</span></Label>
              <Select value={form.road_width_id} onValueChange={(v) => setForm({ ...form, road_width_id: v })}>
                <SelectTrigger data-testid="tp-roadwidth-select"><SelectValue placeholder="Select road width" /></SelectTrigger>
                <SelectContent>
                  {roadWidths.map((r) => <SelectItem key={r.id} value={r.id}>{r.width_label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Phase 211: unified demand-lines editor (new + existing) */}
          <TPDemandLinesEditor lines={lines} onChange={setLines} fys={fys} taxHeads={taxHeads} disabled={readOnly} testIdPrefix="tp-lines"
            autoHint={hasAmountComponents ? "The first line's Current amount auto-calculates from the arrear + dues fields below — you can still edit it before submitting." : null} />
          {needsReason && (
            <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="tp-correction-reason-box">
              <Label htmlFor="tp-correction-reason" className="text-amber-900">Reason for correcting {changedLines.length} existing demand line{changedLines.length === 1 ? "" : "s"} <span className="text-destructive">*</span></Label>
              <Textarea id="tp-correction-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Arrear re-verified from the register; current year dues revised after reassessment" data-testid="tp-correction-reason" />
              <p className="text-[11px] text-amber-900/80">At least 5 characters. Recorded in the demand's correction history (Reports → Demand Corrections) with your name; the ledger is rebuilt and the owner is notified.</p>
            </div>
          )}

          {existing && existing.source !== "third_party" && (fields || []).some((f) => f.required) && (
            <p className="text-[11px] text-muted-foreground -mb-2" data-testid="tp-custom-optional-note">
              This property was not created through Third Party ({(existing.source || "register").replace(/_/g, " ")}) — the third-party fields below are optional for it.
            </p>
          )}
          <TPFieldInputs fields={fields} values={form.custom_data}
            onChange={handleCustomChange}
            onDelete={readOnly ? undefined : (f) => setDeleteTarget(f)} />

          {/* GPS photos */}
          <div className="space-y-2 border rounded-lg p-3">
            <div className="flex items-center justify-between gap-2">
              <Label className="flex items-center gap-1.5"><Camera className="h-4 w-4" /> GPS-tagged photos{existing?.photo_count ? <span className="text-xs text-muted-foreground font-normal">({existing.photo_count} already on this property)</span> : null}</Label>
              <Button type="button" variant="outline" size="sm" disabled={uploadingPhoto}
                onClick={() => fileRef.current?.click()} data-testid="tp-add-photo-button">
                <Camera className="h-4 w-4 mr-1.5" /> {uploadingPhoto ? "Uploading..." : "Capture / Add photo"}
              </Button>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={onPhotoPicked} data-testid="tp-photo-input" />
            </div>
            {photos.length === 0 ? (
              <p className="text-xs text-muted-foreground">Optional — photos are stamped with your current GPS location. No AI processing is applied.{existing ? " New photos are added to the property's existing ones." : ""}</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="tp-photo-list">
                {photos.map((p) => (
                  <div key={p.file_id} className="relative w-24" data-testid={`tp-photo-${p.file_id}`}>
                    <img src={p.previewUrl} alt={p.filename || "photo"} className="w-24 h-20 object-cover rounded-md border" />
                    <button type="button" onClick={() => removePhoto(p.file_id)}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-0.5"
                      title="Remove photo" data-testid={`tp-photo-remove-${p.file_id}`}>
                      <X className="h-3 w-3" />
                    </button>
                    <span className={`flex items-center gap-0.5 text-[10px] mt-0.5 ${p.lat != null ? "text-emerald-700" : "text-amber-700"}`}>
                      <MapPin className="h-3 w-3" /> {p.lat != null ? `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}` : "No GPS"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={busy || readOnly} data-testid="tp-manual-submit-button">
            {readOnly ? "View only — saving disabled"
              : busy ? (existing ? "Saving changes..." : "Creating property...")
                : existing ? `Save property & demand lines${changedLines.length ? ` (${changedLines.length} correction${changedLines.length === 1 ? "" : "s"})` : ""}`
                  : "Create property & enter workflow"}
          </Button>
          </fieldset>
        </form>

        {/* inline field deletion confirm (syncs bulk template on the fly) */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="tp-inline-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete field "{deleteTarget?.label}"?</AlertDialogTitle>
              <AlertDialogDescription>
                It will be removed from this Manual Entry form and its column will disappear from the
                Bulk Entry template immediately. Values already saved on existing properties remain stored.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="tp-inline-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDeleteField}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="tp-inline-delete-confirm-button">Delete field</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

// ---------------- Bulk Entry ----------------
function BulkSection({ code, fields, fixedColumns, readOnly = false }) {
  const storageKey = `tp-bulk-job:${code}:${getAuth()?.user?.id || "staff"}`;
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [trackingError, setTrackingError] = useState(null);
  const [retryToken, setRetryToken] = useState(0);
  const [job, setJob] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || null; } catch { return null; }
  });
  const [stopping, setStopping] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [finishedJob, setFinishedJob] = useState(null);   // {job_id, filename, status, created} — enables Undo
  const [undoOpen, setUndoOpen] = useState(false);
  const [undone, setUndone] = useState(null);             // undo summary once rolled back
  const [reconOpen, setReconOpen] = useState(false);      // Phase 212: Import Reconciliation Report for the finished job
  const busy = sending || !!job;

  const rememberJob = (meta) => {
    // Storage can be unavailable in private/restricted browsers; tracking must still work.
    try { localStorage.setItem(storageKey, JSON.stringify(meta)); } catch { /* optional persistence */ }
    setJob(meta);
  };

  useEffect(() => {
    if (!job?.job_id) return;
    const jobId = job.job_id;
    const jobFilename = job.filename;
    let stopped = false;
    let timer;
    let misses = 0;
    const controller = new AbortController();
    setTrackingError(null);
    setProgress((prev) => prev || { stage: "queued", processed: 0, total: null });
    const clearStoredJob = () => {
      try { localStorage.removeItem(storageKey); } catch { /* optional persistence */ }
      setJob(null);
      setStopping(false);
    };
    const remember = (j) => setFinishedJob({
      job_id: jobId, filename: jobFilename || j.filename, status: j.status, created: j.created || 0,
    });
    const poll = async () => {
      let j;
      try {
        const r = await api.get(`/t/${code}/third-party/bulk/jobs/${jobId}`, {
          timeout: 15000, signal: controller.signal,
        });
        if (stopped) return;
        j = r.data;
        misses = 0;
        setTrackingError(null);
      } catch (err) {
        if (stopped) return;
        if (err.response?.status === 404) {
          setError("This import is no longer in upload history. Check Upload Status before uploading again.");
          setProgress(null);
          clearStoredJob();
          return;
        }
        misses += 1;
        setTrackingError(misses >= 5
          ? "Connection lost while tracking import. The import may still be running on the server. Retry tracking; do not upload the file again."
          : "Reconnecting to your import… Your last recorded progress is shown below.");
        // Stop after repeated misses, preserving job ID + last progress for manual retry.
        if (misses < 5 && ![401, 403].includes(err.response?.status)) {
          timer = setTimeout(poll, 2000);
        }
        return;
      }
      if (j.status === "completed") {
        setResult(j.result);
        setProgress(null);
        remember(j);
        clearStoredJob();
        if (j.result?.failed === 0) toast.success(`${j.result.created} properties created and entered the workflow`);
        else toast.warning(`${j.result?.created || 0} created, ${j.result?.failed || 0} row(s) failed — see errors below`);
        return;
      }
      if (j.status === "cancelled") {
        setResult(j.result || { total_rows: j.total_rows, processed_rows: j.processed || 0, created: j.created || 0,
          failed: j.failed || 0, duplicates: j.duplicates || 0, assigned_to_collectors: j.assigned || 0,
          demands_created: j.demands || 0, stopped: true, remaining_rows: (j.total_rows || 0) - (j.processed || 0),
          created_rows: [], errors: [], note: j.error });
        setProgress(null);
        remember(j);
        clearStoredJob();
        toast.info(`Import stopped — ${j.created || 0} row(s) were created before the stop`);
        return;
      }
      if (j.status === "failed") {
        setError(j.error || "The import failed on the server. Check Upload Status before retrying.");
        setProgress(null);
        remember(j);
        clearStoredJob();
        return;
      }
      if (j.cancel_requested) setStopping(true);
      setProgress({ stage: j.stage || "importing", processed: j.processed || 0, total: j.total_rows });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); controller.abort(); };
  }, [code, storageKey, job?.job_id, job?.filename, retryToken]);

  const stopImport = async () => {
    if (!job?.job_id) return;
    setConfirmStop(false);
    setStopping(true);
    try {
      await api.post(`/t/${code}/third-party/bulk/jobs/${job.job_id}/cancel`);
      toast.info("Stop requested — finishing the current row and saving results…");
    } catch (err) {
      // 409 = already finished / finalizing: the poller will show the final state momentarily.
      if (err.response?.status !== 409) setStopping(false);
      toast[err.response?.status === 409 ? "info" : "error"](errMsg(err));
    }
  };

  const downloadTemplate = async () => {
    try {
      await downloadBlob(`/t/${code}/third-party/template`, "third-party-bulk-template.xlsx");
      toast.success("Template downloaded — columns match the currently configured fields");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const upload = async () => {
    if (busy) return;
    if (!file) return toast.error("Choose the filled template file first");
    setSending(true);
    setUploadPercent(0);
    setResult(null);
    setError(null);
    setTrackingError(null);
    setProgress(null);
    setFinishedJob(null);
    setUndone(null);
    setStopping(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/t/${code}/third-party/bulk`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          if (event.total) setUploadPercent(Math.min(100, Math.round(event.loaded * 100 / event.total)));
        },
      });
      const d = r.data;
      if (d.job_id && d.status !== "completed") {
        rememberJob({ job_id: d.job_id, filename: file.name, started_at: new Date().toISOString() });
        setProgress({ stage: d.stage || "preparing", processed: 0, total: d.total_rows });
      } else {
        // Keep compatibility while the frontend/backend versions are being updated.
        setResult(d);
      }
    } catch (err) {
      const uncertain = !err.response || err.response.status >= 500;
      setError(uncertain
        ? "The server did not confirm this upload. Check Upload Status before trying again to avoid duplicate properties."
        : errMsg(err));
    } finally {
      setSending(false);
    }
  };

  const stageLabel = sending
    ? (uploadPercent < 100 ? `Uploading file… ${uploadPercent}%` : "File sent — waiting for server confirmation…")
    : stopping
      ? `Stopping — finishing the current row and saving results… (${progress?.processed || 0}${progress?.total ? ` / ${progress.total}` : ""} rows)`
      : ({ queued: "Queued on server…", preparing: "Preparing import (reading file, validating columns)…",
          finalizing: "Finalizing (saving results)…" }[progress?.stage]
        || (progress?.total ? `Processing ${progress.processed} / ${progress.total} rows…` : "Processing rows… (row count not available yet)"));
  const canStop = !!job && !sending && progress?.stage !== "finalizing";
  const canUndo = !!finishedJob && finishedJob.created > 0 && !undone;

  return (
    <div className="space-y-4 max-w-3xl">
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">Bulk third-party entry</CardTitle>
          <p className="text-xs text-muted-foreground">
            The template columns regenerate automatically whenever fields are added or removed in Configure Fields.
            Owner Name and Zone are optional (the ward decides the collector; a blank owner is imported as empty).
            Leave Amount blank to auto-calculate it from the arrear/dues columns.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Current template columns</p>
            <div className="flex flex-wrap gap-1.5" data-testid="tp-template-columns">
              {(fixedColumns || []).map((c) => (
                <Badge key={c.key} variant="secondary" className="text-[10px] font-normal">
                  {c.label}{c.required ? " *" : ""}
                </Badge>
              ))}
              {fields.map((f) => (
                <Badge key={f.id} variant="outline" className="text-[10px] font-normal bg-sky-50 text-sky-800 border-sky-200"
                  data-testid={`tp-template-col-${f.key}`}>
                  {f.label}{f.required ? " *" : ""}
                </Badge>
              ))}
            </div>
          </div>
          <Button type="button" variant="outline" className="w-full" onClick={downloadTemplate} data-testid="tp-template-download-button">
            <Download className="h-4 w-4 mr-2" /> Download bulk entry template
          </Button>
          {readOnly ? <ReadOnlyNotice what="the bulk entry template" testId="tp-bulk-readonly-notice" /> : <>
          <div className="space-y-1">
            <Label htmlFor="tp-bulk-file">Filled template (.xlsx / .csv)</Label>
            <Input id="tp-bulk-file" type="file" accept=".xlsx,.xls,.csv" disabled={busy}
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="tp-bulk-file-input" />
            <p className="text-xs text-muted-foreground" data-testid="tp-bulk-upload-hint">
              Upload the filled template. Files are processed in the background; no application file-size or row-count limit.
            </p>
          </div>
          <Button className="w-full" onClick={upload} disabled={busy || !file} data-testid="tp-bulk-upload-button">
            <Upload className="h-4 w-4 mr-2" />
            {sending ? "Uploading…" : job ? "Processing on server…" : "Upload & create properties"}
          </Button>
          </>}
          {busy && (
            <div className="space-y-2" data-testid="tp-bulk-progress" role="status" aria-live="polite">
              <p className="text-sm font-medium" data-testid="tp-bulk-progress-label">{stageLabel}</p>
              <Progress data-testid="tp-bulk-progress-bar" aria-label={stageLabel}
                className="[&>div]:transition-transform [&>div]:duration-200"
                value={sending ? uploadPercent : progress?.total ? Math.min(100, Math.round(progress.processed / progress.total * 100)) : 0} />
              {!sending && !progress?.total && <Skeleton className="h-2 w-full" data-testid="tp-bulk-preparing-skeleton" />}
              {job && <>
                <p className="text-xs text-muted-foreground break-all" data-testid="tp-bulk-job-id">
                  {job.filename} · Job ID: {job.job_id}
                </p>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground" data-testid="tp-bulk-background-hint">
                    You can leave this page — the import continues. Return to Bulk Entry to resume tracking automatically.
                  </p>
                  {canStop && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setConfirmStop(true)} disabled={stopping}
                      className="shrink-0 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                      data-testid="tp-bulk-stop-button">
                      <OctagonX className="h-3.5 w-3.5 mr-1.5" /> {stopping ? "Stopping…" : "Stop import"}
                    </Button>
                  )}
                </div>
              </>}
            </div>
          )}
          <AlertDialog open={confirmStop} onOpenChange={setConfirmStop}>
            <AlertDialogContent data-testid="tp-bulk-stop-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Stop this import?</AlertDialogTitle>
                <AlertDialogDescription>
                  Rows already created ({progress?.processed || 0} processed so far) are kept and enter the workflow as usual;
                  the remaining rows will not be imported. You can undo the created rows afterwards from the result panel
                  or Upload Status — they move to the recycle bin and can be restored.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="tp-bulk-stop-cancel">Keep importing</AlertDialogCancel>
                <AlertDialogAction onClick={stopImport} data-testid="tp-bulk-stop-confirm"
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Stop import
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {finishedJob && (
            <UndoImportDialog code={code} job={finishedJob} open={undoOpen} onOpenChange={setUndoOpen}
              onDone={(u) => setUndone(u)} />
          )}
          {finishedJob?.job_id && (
            <ImportReconciliationDialog code={code} source="third_party" batchId={finishedJob.job_id} filename={finishedJob.filename}
              open={reconOpen} onOpenChange={setReconOpen} testIdPrefix="tp-recon" />
          )}
          {trackingError && (
            <Alert variant={trackingError.startsWith("Connection lost") ? "destructive" : "default"} data-testid="tp-bulk-tracking-error">
              <AlertTitle data-testid="tp-bulk-tracking-error-title">Import tracking interrupted</AlertTitle>
              <AlertDescription className="space-y-3">
                <p data-testid="tp-bulk-tracking-error-message">{trackingError}</p>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => setRetryToken((v) => v + 1)} data-testid="tp-bulk-retry-tracking-button">Retry tracking</Button>
                  <Button variant="outline" data-testid="tp-bulk-copy-job-button" onClick={async () => {
                    try { await navigator.clipboard.writeText(job.job_id); toast.success("Job ID copied"); }
                    catch { toast.error("Could not copy. Select the Job ID shown above."); }
                  }}>Copy Job ID</Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive" data-testid="tp-bulk-error">
              <AlertTitle data-testid="tp-bulk-error-title">Import could not be completed</AlertTitle>
              <AlertDescription className="space-y-3">
                <p data-testid="tp-bulk-error-message">{error}</p>
                {canUndo && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setUndoOpen(true)} data-testid="tp-bulk-error-undo-button"
                    className="bg-background text-foreground hover:bg-muted">
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Undo this import ({finishedJob.created} created)
                  </Button>
                )}
                {undone && (
                  <p className="text-xs" data-testid="tp-bulk-error-undone">
                    Rolled back — {undone.moved} propert{undone.moved === 1 ? "y" : "ies"} moved to the recycle bin.
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card className="shadow-sm" data-testid="tp-bulk-result">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <CardTitle className="text-base font-heading flex items-center gap-2" data-testid="tp-bulk-result-title">
                {result.stopped ? <><OctagonX className="h-4 w-4 text-amber-700" /> Import stopped</> : "Import result"}
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                {finishedJob?.job_id && (
                  <ReconcileButton onClick={() => setReconOpen(true)} label="View reconciliation report" testId="tp-bulk-reconcile-button" />
                )}
                {canUndo && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setUndoOpen(true)} data-testid="tp-bulk-undo-button"
                    className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive">
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Undo this import
                  </Button>
                )}
              </div>
              {undone && (
                <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-800 border-violet-200 gap-1" data-testid="tp-bulk-undone-badge">
                  <ArchiveRestore className="h-3 w-3" /> Rolled back · {undone.moved} in recycle bin
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {result.stopped && (
              <Alert className="border-amber-200 bg-amber-50 text-amber-900 [&>svg]:text-amber-700" data-testid="tp-bulk-stopped-note">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Stopped at row {result.stopped_at_row ?? (result.processed_rows || 0) + 2}</AlertTitle>
                <AlertDescription>
                  {result.processed_rows || 0} of {result.total_rows || 0} rows were processed; {result.remaining_rows || 0} row(s)
                  were not imported. Upload only the remaining rows to continue, or undo this import to remove the created properties.
                </AlertDescription>
              </Alert>
            )}
            <div className={`grid grid-cols-2 gap-2 text-center ${result.stopped ? "sm:grid-cols-5" : "sm:grid-cols-4"}`}>
              <div className="rounded-lg border p-2">
                <p className="text-lg font-semibold text-emerald-700" data-testid="tp-result-created">{result.created}</p>
                <p className="text-[11px] text-muted-foreground">Created</p>
              </div>
              <div className="rounded-lg border p-2">
                <p className="text-lg font-semibold text-destructive" data-testid="tp-result-failed">{result.failed}</p>
                <p className="text-[11px] text-muted-foreground">Failed</p>
              </div>
              <div className="rounded-lg border p-2">
                <p className="text-lg font-semibold" data-testid="tp-result-assigned">{result.assigned_to_collectors}</p>
                <p className="text-[11px] text-muted-foreground">Auto-assigned</p>
              </div>
              <div className="rounded-lg border p-2">
                <p className="text-lg font-semibold" data-testid="tp-result-demands">{result.demands_created}</p>
                <p className="text-[11px] text-muted-foreground">Demands raised</p>
              </div>
              {result.stopped && (
                <div className="rounded-lg border p-2 border-amber-200">
                  <p className="text-lg font-semibold text-amber-700" data-testid="tp-result-remaining">{result.remaining_rows || 0}</p>
                  <p className="text-[11px] text-muted-foreground">Not imported</p>
                </div>
              )}
            </div>
            {result.created_rows?.length > 0 && (
              <div>
                <p className="text-xs font-medium flex items-center gap-1 text-emerald-700 mb-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Created properties
                </p>
                <div className="max-h-48 overflow-y-auto border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="h-8 text-xs">Row</TableHead>
                        <TableHead className="h-8 text-xs">PIN</TableHead>
                        <TableHead className="h-8 text-xs">Owner</TableHead>
                        <TableHead className="h-8 text-xs">Assigned to</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.created_rows.map((c) => (
                        <TableRow key={c.row}>
                          <TableCell className="py-1.5 text-xs">{c.row}</TableCell>
                          <TableCell className="py-1.5 text-xs font-mono">{c.pin}</TableCell>
                          <TableCell className="py-1.5 text-xs">{c.owner}</TableCell>
                          <TableCell className="py-1.5 text-xs">{c.assigned_to || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            {result.errors?.length > 0 && (
              <div>
                <p className="text-xs font-medium flex items-center gap-1 text-destructive mb-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> Row errors (fix these rows and re-upload only them)
                </p>
                <div className="max-h-48 overflow-y-auto border rounded-md">
                  <Table data-testid="tp-error-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="h-8 text-xs w-16">Row</TableHead>
                        <TableHead className="h-8 text-xs">Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.errors.map((e2) => (
                        <TableRow key={e2.row}>
                          <TableCell className="py-1.5 text-xs">{e2.row}</TableCell>
                          <TableCell className="py-1.5 text-xs text-destructive">{e2.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------- Bulk Update by PIN (Phase 102) ----------------
// Steps: 0 pick file → 1 preview & select rows → 2 applying → 3 result
function BulkUpdateSection({ code, fields, readOnly = false }) {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);          // {update_id, filename, summary, rows, columns}
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [flagFilter, setFlagFilter] = useState(null);   // Phase 161: area-flag banner → preview filter
  const [result, setResult] = useState(null);            // apply summary
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      await downloadBlob(`/t/${code}/third-party/bulk-update/template`, "third-party-bulk-update-template.xlsx");
      toast.success("Bulk Update template downloaded — fill only the cells you want to change");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const runPreview = async () => {
    if (!file) return toast.error("Choose the filled Bulk Update template first");
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/t/${code}/third-party/bulk-update/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      setPreview(d);
      // default: every processable row (valid + warnings) ticked; error rows can never be applied
      setSelectedRows(new Set((d.rows || []).filter((x) => x.status !== "error").map((x) => x.row_number)));
      setStep(1);
      const s = d.summary || {};
      if (s.error_rows > 0) toast.warning(`${s.valid_rows} row(s) ready, ${s.error_rows} locked with errors — review the preview`);
      else toast.success(`${s.valid_rows} row(s) ready to update — review and confirm`);
    } catch (err) {
      setError(errMsg(err));
    } finally { setBusy(false); }
  };

  const applySelected = async () => {
    if (!preview?.update_id) return;
    if (selectedRows.size === 0) return toast.error("Select at least one row to update");
    setBusy(true);
    setStep(2);
    setError(null);
    try {
      const r = await api.post(`/t/${code}/third-party/bulk-update/${preview.update_id}/apply`, { selected_rows: [...selectedRows] });
      setResult(r.data.summary);
      setStep(3);
      const s = r.data.summary || {};
      if (s.failed_rows > 0) toast.warning(`${s.updated_rows} row(s) updated, ${s.failed_rows} failed`);
      else toast.success(`${s.updated_rows} propert${s.updated_rows === 1 ? "y" : "ies"} updated${s.demands_created ? ` · ${s.demands_created} demand(s) added` : ""}`);
    } catch (err) {
      setError(errMsg(err));
      setStep(1);
    } finally { setBusy(false); }
  };

  const reset = () => {
    setStep(0); setFile(null); setPreview(null); setSelectedRows(new Set()); setResult(null); setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const rows = preview?.rows || [];
  const summary = preview?.summary || {};
  const processable = rows.filter((r) => r.status !== "error").length;
  const picked = rows.filter((r) => selectedRows.has(r.row_number));
  const pickedChanges = picked.reduce((n, r) => n + (r.changes?.length || 0), 0);
  const pickedDemands = picked.filter((r) => r.demand).length;
  const pickedAmount = picked.reduce((n, r) => n + (r.demand?.amount || 0), 0);
  const pickedNoop = picked.filter((r) => r.no_op).length;

  return (
    <div className="space-y-4">
      {step === 0 && (
        <Card className="shadow-sm max-w-3xl" data-testid="tp-bulk-update-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" /> Bulk update existing third-party entries
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Rows are matched to existing third-party properties by <b>PIN</b>. Fill only the cells you want to change —
              blank cells leave the current value untouched. You will preview every row and choose which ones to apply before anything is saved.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Template columns</p>
              <div className="flex flex-wrap gap-1.5" data-testid="tp-bulk-update-columns">
                <Badge variant="secondary" className="text-[10px] font-normal">PIN *</Badge>
                {["Owner Name", "Father/Spouse Name", "Mobile", "House No", "Address", "Zone", "Ward", "Mohalla"].map((c) => (
                  <Badge key={c} variant="secondary" className="text-[10px] font-normal">{c}</Badge>
                ))}
                {fields.map((f) => (
                  <Badge key={f.id} variant="outline" className="text-[10px] font-normal bg-sky-50 text-sky-800 border-sky-200"
                    data-testid={`tp-bulk-update-col-${f.key}`}>{f.label}</Badge>
                ))}
                <Badge variant="outline" className="text-[10px] font-normal bg-emerald-50 text-emerald-800 border-emerald-200">Financial Year</Badge>
                <Badge variant="outline" className="text-[10px] font-normal bg-emerald-50 text-emerald-800 border-emerald-200">Amount</Badge>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Add a demand for a row by filling <b>both</b> Financial Year and Amount. Zone / Ward / Mohalla names not yet in master data are created automatically.
              </p>
            </div>
            <Button type="button" variant="outline" className="w-full" onClick={downloadTemplate} data-testid="tp-bulk-update-template-button">
              <Download className="h-4 w-4 mr-2" /> Download bulk update template
            </Button>
            {readOnly ? <ReadOnlyNotice what="the bulk update template" testId="tp-bulk-update-readonly-notice" /> : <>
            <div className="space-y-1">
              <Label htmlFor="tp-bulk-update-file">Filled template (.xlsx / .csv)</Label>
              <Input id="tp-bulk-update-file" ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" disabled={busy}
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="tp-bulk-update-file-input" />
              <p className="text-xs text-muted-foreground">Up to 5,000 rows per file. Nothing is changed until you confirm in the preview.</p>
            </div>
            {error && (
              <Alert variant="destructive" data-testid="tp-bulk-update-error">
                <AlertTitle>Could not read the file</AlertTitle>
                <AlertDescription data-testid="tp-bulk-update-error-message">{error}</AlertDescription>
              </Alert>
            )}
            <Button className="w-full" onClick={runPreview} disabled={busy || !file} data-testid="tp-bulk-update-preview-button">
              <Upload className="h-4 w-4 mr-2" /> {busy ? "Reading file & matching PINs…" : "Upload & preview changes"}
            </Button>
            </>}
          </CardContent>
        </Card>
      )}

      {step === 1 && preview && (
        <Card className="shadow-sm" data-testid="tp-bulk-update-preview-card">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" /> Preview &amp; select rows
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5" data-testid="tp-bulk-update-filename">
                  {preview.filename} · {summary.total_rows} row{summary.total_rows === 1 ? "" : "s"}. Filter by zone, ward or mohalla and untick anything that should not be updated.
                  Rows with errors are locked and always skipped.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={reset} disabled={busy} data-testid="tp-bulk-update-change-file">
                <X className="h-4 w-4 mr-1" /> Choose another file
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-3 text-center">
                <CheckCircle2 className="h-4 w-4 mx-auto text-emerald-600 mb-1" />
                <p className="text-xl font-semibold text-emerald-700 tabular-nums" data-testid="tp-bulk-update-valid-rows">{summary.valid_rows ?? 0}</p>
                <p className="text-[11px] text-emerald-700">Rows ready to update</p>
              </div>
              <div className="rounded-xl border bg-amber-50 border-amber-200 p-3 text-center">
                <AlertTriangle className="h-4 w-4 mx-auto text-amber-600 mb-1" />
                <p className="text-xl font-semibold text-amber-700 tabular-nums" data-testid="tp-bulk-update-warning-rows">{summary.warning_rows ?? 0}</p>
                <p className="text-[11px] text-amber-700">Rows with warnings</p>
              </div>
              <div className="rounded-xl border bg-red-50 border-red-200 p-3 text-center">
                <XCircle className="h-4 w-4 mx-auto text-red-600 mb-1" />
                <p className="text-xl font-semibold text-red-700 tabular-nums" data-testid="tp-bulk-update-error-rows">{summary.error_rows ?? 0}</p>
                <p className="text-[11px] text-red-700">Rows with errors (locked)</p>
              </div>
              <div className="rounded-xl border bg-sky-50 border-sky-200 p-3 text-center">
                <Pencil className="h-4 w-4 mx-auto text-sky-600 mb-1" />
                <p className="text-xl font-semibold text-sky-700 tabular-nums" data-testid="tp-bulk-update-field-changes">{summary.field_changes ?? 0}</p>
                <p className="text-[11px] text-sky-700">Field changes · {summary.demands ?? 0} demand{summary.demands === 1 ? "" : "s"}</p>
              </div>
            </div>

            {summary.area_flags?.rows > 0 && (
              <AreaFlagsBanner summary={summary.area_flags} total={summary.total_rows} onFilter={(code) => setFlagFilter(code)} testId="tp-area-flags-banner" />
            )}
            <UploadPreviewTable rows={rows} selected={selectedRows} onChange={setSelectedRows} variant="tp-update"
              flagFilter={flagFilter} onFlagFilterChange={setFlagFilter} />

            {error && (
              <Alert variant="destructive" data-testid="tp-bulk-update-apply-error">
                <AlertTitle>Update could not be applied</AlertTitle>
                <AlertDescription data-testid="tp-bulk-update-apply-error-message">{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3" data-testid="tp-bulk-update-confirm-bar">
              <div className="text-sm" data-testid="tp-bulk-update-selection-summary">
                <b className="tabular-nums">{selectedRows.size}</b> of {processable} processable rows selected
                <span className="text-xs text-muted-foreground"> · {pickedChanges} field change{pickedChanges === 1 ? "" : "s"} · {pickedDemands} demand{pickedDemands === 1 ? "" : "s"}
                  {pickedAmount > 0 ? ` (${fmtINR(pickedAmount)})` : ""}
                  {pickedNoop > 0 ? ` · ${pickedNoop} with nothing to update` : ""}
                  {processable - selectedRows.size > 0 ? ` · ${processable - selectedRows.size} deselected` : ""}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={reset} disabled={busy} data-testid="tp-bulk-update-cancel">Cancel</Button>
                <Button onClick={applySelected} disabled={busy || selectedRows.size === 0} data-testid="tp-bulk-update-apply-button">
                  Confirm — update {selectedRows.size} selected row{selectedRows.size === 1 ? "" : "s"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && <Spinner label="Applying changes to the selected properties…" />}

      {step === 3 && result && (
        <Card className="shadow-sm" data-testid="tp-bulk-update-result">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-3" />
            <h2 className="text-xl font-semibold font-heading" data-testid="tp-bulk-update-result-title">Bulk update applied</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {preview?.filename} · {result.total_rows} row{result.total_rows === 1 ? "" : "s"} in file
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 max-w-2xl mx-auto">
              {[
                ["Properties updated", result.updated_rows, "tp-bulk-update-result-updated", "text-emerald-700"],
                ["Field changes", result.field_changes, "tp-bulk-update-result-changes", ""],
                ["Demands added", result.demands_created, "tp-bulk-update-result-demands", ""],
                ["Failed", result.failed_rows, "tp-bulk-update-result-failed", result.failed_rows > 0 ? "text-destructive" : ""],
              ].map(([l, v, tid, cls]) => (
                <div key={l} className="rounded-xl border p-4">
                  <p className={`text-2xl font-semibold tabular-nums ${cls}`} data-testid={tid}>{v ?? 0}</p>
                  <p className="text-xs text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
            {(result.deselected_rows > 0 || result.error_rows > 0) && (
              <p className="mt-4 text-sm text-muted-foreground" data-testid="tp-bulk-update-result-skipped">
                {result.deselected_rows > 0 && <><b className="text-foreground tabular-nums">{result.deselected_rows}</b> row{result.deselected_rows === 1 ? " was" : "s were"} deselected in the preview and left untouched.</>}
                {result.deselected_rows > 0 && result.error_rows > 0 && " "}
                {result.error_rows > 0 && <><b className="text-foreground tabular-nums">{result.error_rows}</b> locked row{result.error_rows === 1 ? "" : "s"} with errors {result.error_rows === 1 ? "was" : "were"} skipped.</>}
              </p>
            )}
            <Button className="mt-6" onClick={reset} data-testid="tp-bulk-update-another-button">
              <RotateCcw className="h-4 w-4 mr-2" /> Update another file
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------- Root component ----------------
export function ThirdPartyEntry({ code, readOnly = false }) {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/t/${code}/third-party/fields`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(errMsg(e)); setData({ fields: [], fixed_columns: [] }); });
  }, [code]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Spinner />;
  const fields = data.fields || [];

  return (
    <Tabs defaultValue="manual">
      <TabsList className="mb-4" data-testid="tp-inner-tabs">
        <TabsTrigger value="manual" data-testid="tp-tab-manual">Manual Entry</TabsTrigger>
        <TabsTrigger value="bulk" data-testid="tp-tab-bulk">Bulk Entry</TabsTrigger>
        <TabsTrigger value="bulk-update" data-testid="tp-tab-bulk-update">Bulk Update</TabsTrigger>
        <TabsTrigger value="payments" data-testid="tp-tab-import-payments">Import Payments</TabsTrigger>
        <TabsTrigger value="fields" data-testid="tp-tab-fields">
          Configure Fields {fields.length > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px]">{fields.length}</Badge>}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="manual"><ManualSection code={code} fields={fields} onChanged={load} readOnly={readOnly} /></TabsContent>
      <TabsContent value="bulk"><BulkSection key={`${code}:${getAuth()?.user?.id || "staff"}`} code={code} fields={fields} fixedColumns={data.fixed_columns} readOnly={readOnly} /></TabsContent>
      <TabsContent value="bulk-update"><BulkUpdateSection code={code} fields={fields} readOnly={readOnly} /></TabsContent>
      <TabsContent value="payments"><PaymentImportSection code={code} scope="third_party" readOnly={readOnly} /></TabsContent>
      <TabsContent value="fields"><ConfigureFieldsSection code={code} fields={fields} onChanged={load} readOnly={readOnly} /></TabsContent>
    </Tabs>
  );
}

export default ThirdPartyEntry;
